import { useMemo } from 'react';
import { StyleSheet, View, ViewProps } from 'react-native';
import { useTranslation } from 'react-i18next';

import { filterUsersByNames, getName } from '@pletly/shared';

import { Input } from '../Input';
import { Text } from '../Text';
import { useInput } from '../../hooks';
import { spacing } from '../../theme';
import { SearchFriends } from '../../assets';
import { ContactSelectProps, ContactsSelect, Contact } from './ContactsSelect';
import { withTranslation } from '../../locales/hoc';

export type ContactsSelectWithSearchProps = Omit<ContactSelectProps, 'contacts'> & {
	contacts: (Omit<Contact, 'name'> & {
		username: string;
		firstName?: string | null;
		lastName?: string | null;
	})[];
} & ViewProps & {
		searchLabel?: string;
		listTitle?: string;
	};

export const ContactsSelectWithSearchComponent = ({
	mode,
	contacts,
	selectedIds,
	setSelectedIds,
	style,
	searchLabel,
	listTitle,
	...viewProps
}: ContactsSelectWithSearchProps) => {
	const { t } = useTranslation();

	const contactNameInput = useInput();

	const contactsToDisplay = useMemo(
		() =>
			filterUsersByNames(contacts, contactNameInput.value).map(({ id, photoSource, ...names }) => ({
				id,
				photoSource,
				name: getName(names),
			})),
		[contacts, contactNameInput.value],
	);

	return (
		<View style={[styles.contactsSelect, style]} {...viewProps}>
			<Input
				placeholder={searchLabel || t('contacts-select.input-search-label')}
				label={searchLabel || t('contacts-select.input-search-label')}
				accessoryLeft={<SearchFriends />}
				{...contactNameInput}
			/>

			<View style={styles.list}>
				<Text category="h5">{listTitle || t('contacts-select.list-title')}</Text>

				<ContactsSelect
					contacts={contactsToDisplay}
					mode={mode}
					selectedIds={selectedIds}
					setSelectedIds={setSelectedIds}
				/>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	contactsSelect: {
		gap: spacing(3),
	},
	list: {
		flex: 1,
	},
});

export const ContactsSelectWithSearch = withTranslation(ContactsSelectWithSearchComponent);
